import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { downloadTikTokVideo } from '../features/thread/threadSlice';
import toast from 'react-hot-toast';
import requests from '../api/requests';

const SubThread = ({ 
  index, 
  value = {}, 
  onChange, 
  onRemove,
  isSearching = false,
  pendingDownloads = {},
  downloadedVideos = {}
}) => {
  const dispatch = useDispatch();
  const [isVideoSearching, setIsVideoSearching] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);

  const handleChange = (field, fieldValue) => {
    if (onChange) {
      onChange(index, field, fieldValue);
    }
  };

  const handleVideoSearch = async (url) => {
    try {
      setIsVideoSearching(true);
      
      if (!url) return;

      // Determine if it's a TikTok or Instagram URL
      const isTikTok = url.includes('tiktok.com');
      const isInstagram = url.includes('instagram.com');

      // If it's a new social media URL, clear the selected video
      if (isTikTok || isInstagram) {
        setSelectedVideo(null);
      } else {
        // If it's not a social media URL, just update the value without searching
        handleChange("video", url);
        return;
      }

      if (!isTikTok && !isInstagram) {
        toast.error('Please enter a valid TikTok or Instagram URL');
        return;
      }

      let response;
      if (isTikTok) {
        response = await requests.tiktok.searchVideoByUrl({ videoUrl: url });
      } else {
        response = await requests.instagram.searchVideoByUrl({ videoUrl: url });
      }

      if (response.data) {
        if (isInstagram && response.data[0]?.urls?.[0]) {
          setSelectedVideo({
            ...response.data[0],
            type: 'instagram'
          });
        } else {
          setSelectedVideo({
            ...response.data,
            type: 'tiktok'
          });
        }
        handleChange("video", url);
      }
    } catch (error) {
      console.error('Error searching video:', error);
      toast.error('Failed to search video');
    } finally {
      setIsVideoSearching(false);
    }
  };

  const handleDownloadVideo = async () => {
    if (!selectedVideo) return;

    try {
      setIsVideoSearching(true);
      let downloadData;

      if (selectedVideo.type === 'instagram') {
        downloadData = await dispatch(downloadTikTokVideo({
          videoUrl: selectedVideo.urls[0].url,
          videoMetadata: {
            title: selectedVideo.meta?.title || 'instagram_video',
            author: selectedVideo.meta?.author || '',
            cover: selectedVideo.pictureUrl || '',
            description: selectedVideo.meta?.description || '',
            playUrl: selectedVideo.urls[0].url,
            source: 'instagram',
            fileName: `instagram_${Date.now()}_${selectedVideo.meta?.author || 'video'}`
          }
        })).unwrap();
      } else {
        downloadData = await dispatch(downloadTikTokVideo({
          videoUrl: selectedVideo.videoUrl,
          videoMetadata: {
            title: selectedVideo.title || 'tiktok_video',
            author: selectedVideo.author || '',
            cover: selectedVideo.cover || '',
            duration: selectedVideo.duration || '',
            views: selectedVideo.views || 0,
            description: selectedVideo.description || '',
            playUrl: selectedVideo.playUrl || '',
            source: 'tiktok',
            fileName: `tiktok_${Date.now()}_${selectedVideo.author || 'video'}`
          }
        })).unwrap();
      }

      if (downloadData.videoUrl) {
        handleChange("video", downloadData.videoUrl);
        toast.success('Video downloaded successfully');
      } else {
        throw new Error('Failed to download video');
      }
    } catch (error) {
      console.error('Error downloading video:', error);
      toast.error('Failed to download video');
    } finally {
      setIsVideoSearching(false);
      setSelectedVideo(null);
    }
  };

  return (
    <div className="border p-4 rounded-lg mb-4">
      <div className="flex justify-between items-center mb-4">
        <h4 className="font-medium">Sub-thread {index + 1}</h4>
        <button
          type="button"
          onClick={() => onRemove && onRemove(index)}
          className="text-red-500 hover:text-red-700"
        >
          Remove
        </button>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Title
          </label>
          <input
            type="text"
            value={value.title || ''}
            onChange={(e) => handleChange("title", e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Booking Link
          </label>
          <input
            type="text"
            value={value.bookingLink || ''}
            onChange={(e) => handleChange("bookingLink", e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Rating
          </label>
          <input
            type="number"
            value={value.rating === undefined || value.rating === null ? "" : value.rating}
            onChange={e => handleChange("rating", e.target.value === "" ? "" : parseFloat(e.target.value))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Min Rate
          </label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={value.minRate || 0}
            onChange={(e) => handleChange("minRate", parseFloat(e.target.value) || 0)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Max Rate
          </label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={value.maxRate || 0}
            onChange={(e) => handleChange("maxRate", parseFloat(e.target.value) || 0)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Tiktok or Instagram Link
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={value.video || ''}
              onChange={(e) => handleVideoSearch(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="Enter TikTok or Instagram URL"
            />
            {isVideoSearching && (
              <div className="mt-1">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
              </div>
            )}
          </div>
          
          {/* Download Button - Show when video is found */}
          {selectedVideo && (
            <div className="mt-2">
              <button
                onClick={handleDownloadVideo}
                className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
                disabled={isVideoSearching}
              >
                <svg 
                  className="w-5 h-5" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" 
                  />
                </svg>
                {isVideoSearching ? 'Downloading...' : 'Download Video'}
              </button>
            </div>
          )}
          
          {/* Video Preview - Show when video is downloaded */}
          {value.video && !selectedVideo && (
            <div className="mt-2">
              <video
                key={value.video}
                src={`${process.env.REACT_APP_BASE_URL}${value.video}`}
                controls
                className="h-32 w-full object-cover rounded"
                onError={(e) => {
                  console.error('Video error:', e);
                  handleChange("video", "");
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubThread; 